import { useState } from "react";
import { Eyebrow } from "@/components/media/Sections";
import { embedUrl } from "@/components/media/VideoEmbed";
import { ExternalLink, Instagram, Play } from "lucide-react";

/**
 * One reel. Tries the real embed; if the platform blocks the iframe the
 * thumbnail card stays and the reel opens on Instagram instead.
 */
const Reel = ({ reel }) => {
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const embed = embedUrl(reel.url);
  const canEmbed = !!embed && !blocked;

  return (
    <article
      data-testid={`reel-${reel.id}`}
      className="group flex flex-col border border-border bg-[#141414] transition-colors hover:border-primary/60"
    >
      {canEmbed && playing ? (
        <iframe
          data-testid={`reel-iframe-${reel.id}`}
          src={`${embed}${embed.includes("?") ? "&" : "?"}autoplay=1`}
          title={reel.caption || "Smash reel"}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          scrolling="no"
          onError={() => setBlocked(true)}
          className="w-full border-0 bg-black"
          style={{ aspectRatio: "9/16" }}
        />
      ) : (
        <button
          data-testid={`reel-play-${reel.id}`}
          onClick={() => (canEmbed ? setPlaying(true) : window.open(reel.url, "_blank"))}
          className="relative w-full overflow-hidden bg-black"
          style={{ aspectRatio: "9/16" }}
        >
          {reel.thumbnail ? (
            <img src={reel.thumbnail} alt="" className="h-full w-full object-cover opacity-75" />
          ) : (
            <span className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,#2a2a12,#0a0a0a)]" />
          )}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform duration-300 group-hover:scale-110">
              <Play size={20} fill="currentColor" />
            </span>
          </span>
        </button>
      )}
      <div className="flex flex-1 flex-col p-4">
        {reel.caption ? (
          <p className="text-sm leading-snug text-white">{reel.caption}</p>
        ) : null}
        <a
          href={reel.url}
          target="_blank"
          rel="noreferrer"
          data-testid={`reel-link-${reel.id}`}
          className="mono mt-auto pt-4 inline-flex items-center gap-1.5 text-[10px] tracking-widest text-primary hover:underline"
        >
          WATCH ON INSTAGRAM <ExternalLink size={11} />
        </a>
      </div>
    </article>
  );
};

export const ReelWall = ({ intro, reels }) => {
  if (!reels?.length) return null;
  return (
    <>
      <Eyebrow>PUBLISHED, NOT POLISHED</Eyebrow>
      <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{intro.title}</h2>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{intro.intro}</p>
      {intro.handle ? (
        <a
          href={intro.handle_url || "#"}
          target="_blank"
          rel="noreferrer"
          data-testid="reels-handle"
          className="mono mt-4 inline-flex items-center gap-2 text-[11px] tracking-widest text-primary hover:underline"
        >
          <Instagram size={13} /> {intro.handle}
        </a>
      ) : null}
      <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {reels.map((r) => (
          <Reel key={r.id} reel={r} />
        ))}
      </div>
    </>
  );
};
